/**
 * TaskDrawer: one task, every action (DD-017, DD-051). From the left on
 * desktop, a bottom sheet on phones. View mode paints from the row's task,
 * then loads `GET /api/tasks/<id>` and shows its fresher copy; edit and
 * create share one form. One drawer per list, which owns its state through
 * useTaskDrawer. Capabilities gate what shows (drawerSectionsFor), the
 * routes enforce: the creator portal gets view mode only and sends no
 * admin request. The People section lists the owner and the tags (TD-103).
 * Create mode holds a voice note until the task exists, then the notes
 * section sends it as the first note (TaskCreateVoice).
 * In the client portal the drawer edits through the portal routes, which
 * tasksService picks from the HttpClient context.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { EditIcon, TrashIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { Drawer } from "@/components/ui/Drawer";
import type { TrailCrumb } from "@/core/navigation/trail";
import { useAppStore } from "@/core/store/useAppStore";
import { ClientBadge } from "@/domains/Client/components/ClientBadge";
import { cn } from "@/utils/cn";
import { TaskAssigneePill, TaskCheckButton } from "../components/TaskRowParts";
import {
  useClientPeopleQuery,
  useCreateTaskForClientMutation,
  invalidateTasksAfterGone,
  useDeleteTaskMutation,
  useTaskQuery,
  useToggleTask,
  useUpdateTaskMutation,
} from "../hooks/useTasks";
import { TaskCapabilities, drawerSectionsFor } from "../taskCapabilities";
import { isNotFound } from "../taskQueryRules";
import { TaskAssigneeOption } from "../types";
import { TaskCreateVoice } from "./TaskCreateVoice";
import { TASK_FORM_ID, TaskForm } from "./TaskForm";
import { TaskLinksSection } from "./TaskLinksSection";
import { TaskNotesSection } from "./TaskNotesSection";
import { TaskPeopleSection } from "./TaskPeopleSection";
import { type PendingLeave, pathAfterDiscard } from "./taskNotes";
import {
  TaskClientOption,
  assignablePeople,
  canCreateTaskDraft,
  canSaveTaskDraft,
  clientPickerOptions,
  createTaskRequest,
  taskEditPatch,
} from "./taskDrawerForm";
import type { TaskDrawerController } from "./useTaskDrawer";

/** Router state for a followed link, so the breadcrumb leads back
 * (core/navigation/trail). */
export interface TaskNavOptions {
  state: { trail: TrailCrumb[] };
}

interface TaskDrawerProps {
  drawer: TaskDrawerController;
  capabilities: TaskCapabilities;
  /** The owner's trail options: a followed link keeps the breadcrumb. */
  navOptions?: TaskNavOptions;
  /** The Tasks tab's clients: view mode shows the task's ClientBadge and
   * create mode asks for the client. */
  clients?: TaskClientOption[];
  /** The brief shot list offers no client contact as assignee (DD-022). */
  excludeClientContacts?: boolean;
  /** The client portal: a save that returns a task the client no longer
   * sees (`clientVisible: false`) closes the drawer, then calls this. The
   * list refetches, so the row leaves (DD-053). */
  onTeamOnly?: () => void;
}

const NO_PEOPLE: TaskAssigneeOption[] = [];
const TITLE = "text-lg font-semibold text-gray-900";

export function TaskDrawer({
  drawer,
  capabilities,
  navOptions,
  clients,
  excludeClientContacts = false,
  onTeamOnly,
}: TaskDrawerProps) {
  const { state, synced, close } = drawer;
  const sections = drawerSectionsFor(capabilities);
  // A recording lives only in the notes section or the create form. Closing
  // or leaving would drop it, so the drawer asks first, as Home's note
  // popover does (DD-046).
  const [unsentRecording, setUnsentRecording] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<PendingLeave | null>(null);
  const handleUnsentChange = useCallback((hasUnsent: boolean) => {
    setUnsentRecording(hasUnsent);
    if (!hasUnsent) setPendingLeave(null);
  }, []);
  const requestClose = useCallback(() => {
    if (unsentRecording) setPendingLeave({ kind: "close" });
    else close();
  }, [unsentRecording, close]);
  const queryClient = useQueryClient();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const addToast = useAppStore((store) => store.addToast);
  const toggle = useToggleTask();
  const updateTask = useUpdateTaskMutation();
  const deleteTask = useDeleteTaskMutation();
  const createTask = useCreateTaskForClientMutation();

  const openTask = state.mode === "view" || state.mode === "edit" ? state.task : null;
  const detail = useTaskQuery(openTask?.id, sections.links && state.mode === "view");
  // The draft's client: in edit mode a picked client loads its own people (DD-049).
  const formClientId = state.mode === "edit" || state.mode === "create" ? state.draft.clientId : null;
  const { data: allPeople = NO_PEOPLE, isLoading: peopleLoading } = useClientPeopleQuery(
    formClientId ?? undefined
  );
  const people = useMemo(
    () => assignablePeople(allPeople, excludeClientContacts),
    [allPeople, excludeClientContacts]
  );
  const clientOptions = useMemo(() => (clients ? clientPickerOptions(clients) : undefined), [clients]);

  // The detail carries the freshest copy of the task: show it once it lands.
  useEffect(() => {
    if (detail.data) synced(detail.data.task);
  }, [detail.data, synced]);

  // Deleted elsewhere: close at the first 404, say so, and refresh the
  // lists so the stale row goes too.
  const openTaskId = openTask?.id;
  useEffect(() => {
    if (!openTaskId || !isNotFound(detail.error)) return;
    addToast({ message: "Questo task è stato eliminato.", type: "error" });
    void invalidateTasksAfterGone(queryClient, openTaskId);
    close();
  }, [openTaskId, detail.error, addToast, queryClient, close]);

  const navigate = useNavigate();
  // A followed link closes the drawer, then navigates (DD-052).
  const follow = useCallback(
    (path: string) => {
      if (unsentRecording) {
        setPendingLeave({ kind: "follow", path });
        return;
      }
      close();
      navigate(path, navOptions);
    },
    [unsentRecording, close, navigate, navOptions]
  );

  const showError = (message: string) => addToast({ message, type: "error" });

  const handleSave = () => {
    if (state.mode !== "edit" || !canSaveTaskDraft(state.task, state.draft, capabilities.canTagTeam)) return;
    const saved = drawer.onSaved();
    updateTask.mutate(
      { taskId: state.task.id, data: taskEditPatch(state.task, state.draft, capabilities.canTagTeam) },
      {
        onSuccess: (task) => {
          // The client's own edit handed the task to the team: it leaves this list.
          if (onTeamOnly && !task.clientVisible) {
            close();
            onTeamOnly();
            return;
          }
          saved(task);
        },
        onError: () => showError("Impossibile salvare il task."),
      }
    );
  };

  const handleCreate = () => {
    if (state.mode !== "create" || state.draft.clientId === null || !canCreateTaskDraft(state.draft)) {
      return;
    }
    // Enter in the title submits too: a running recording would be cut off.
    if (unsentRecording && state.recording === null) return;
    createTask.mutate(
      {
        clientId: state.draft.clientId,
        data: createTaskRequest(state.draft, people, state.createDefaults.links),
      },
      { onSuccess: drawer.onCreated(), onError: () => showError("Impossibile creare il task.") }
    );
  };

  const askDelete = () => {
    deleteTask.reset();
    drawer.confirmDelete();
  };

  // The drawer closes only once the delete succeeded (spec, Error handling).
  // A 404 means it is already gone: same outcome as the detail's 404.
  const handleDelete = (taskId: string) =>
    deleteTask.mutate(taskId, {
      onSuccess: close,
      onError: (error) => {
        if (!isNotFound(error)) return;
        addToast({ message: "Questo task è stato eliminato.", type: "error" });
        void invalidateTasksAfterGone(queryClient, taskId);
        close();
      },
    });

  // View mode opens on the title, not the first tabbable (the check circle,
  // where a second Enter would toggle the task). Forms keep their own autoFocus.
  const focusTitleInView = (event: Event) => {
    if (state.mode !== "view") return;
    event.preventDefault();
    titleRef.current?.focus();
  };

  let header: React.ReactNode = null;
  let body: React.ReactNode = null;
  let footer: React.ReactNode = null;

  if (state.mode === "view") {
    const task = state.task;
    const done = task.status === "done";
    const clientName = clients?.find((client) => client.id === task.clientId)?.clientName;
    header = (
      <div className="flex items-start gap-3">
        <div className="pt-1">
          <TaskCheckButton
            task={task}
            toggleable={capabilities.canToggle(task)}
            onToggle={(clicked) => toggle(clicked, synced)}
          />
        </div>
        <h2
          ref={titleRef}
          tabIndex={-1}
          className={cn(TITLE, "min-w-0 break-words focus:outline-hidden", done && "text-gray-400 line-through")}
        >
          {task.title}
        </h2>
      </div>
    );
    body = (
      <div className="space-y-6">
        {task.description && (
          <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{task.description}</p>
        )}
        {clientName && (
          <div>
            <ClientBadge name={clientName} size="sm" />
          </div>
        )}
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">Assegnato a</span>
          <TaskAssigneePill assignee={task.assignee} owner />
        </div>
        <TaskPeopleSection
          tags={task.people}
          clientVisible={capabilities.canSeeVisibility && task.clientVisible}
        />
        {sections.links && (
          <TaskLinksSection
            key={`links-${task.id}`}
            task={task}
            detail={detail.data}
            failed={detail.isError}
            onFollow={follow}
          />
        )}
        {sections.notes && (
          <TaskNotesSection
            key={`notes-${task.id}`}
            taskId={task.id}
            onUnsentChange={handleUnsentChange}
            firstNote={state.firstNote}
            onFirstNoteTaken={drawer.firstNoteTaken}
          />
        )}
      </div>
    );
    if (state.confirmingDelete) {
      footer = (
        <>
          <div className="mr-auto min-w-0">
            <p className="text-sm font-medium text-gray-900">Eliminare questo task?</p>
            {deleteTask.isError && (
              <p className="text-xs text-red-600">Impossibile eliminare il task.</p>
            )}
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={drawer.cancelDelete}
            disabled={deleteTask.isPending}
          >
            Annulla
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => handleDelete(task.id)}
            isLoading={deleteTask.isPending}
          >
            Elimina
          </Button>
        </>
      );
    } else if (sections.edit || sections.delete) {
      footer = (
        <>
          {sections.delete && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={askDelete}
              disabled={unsentRecording}
              className="mr-auto text-red-600"
            >
              <TrashIcon className="w-4 h-4 mr-1.5" />
              Elimina
            </Button>
          )}
          {sections.edit && (
            <Button type="button" variant="secondary" size="sm" onClick={drawer.edit} disabled={unsentRecording}>
              <EditIcon className="w-4 h-4 mr-1.5" />
              Modifica
            </Button>
          )}
        </>
      );
    }
  } else if (state.mode === "edit" || state.mode === "create") {
    const creating = state.mode === "create";
    const canSubmit =
      state.mode === "create" ? canCreateTaskDraft(state.draft) : canSaveTaskDraft(state.task, state.draft, capabilities.canTagTeam);
    const pending = creating ? createTask.isPending : updateTask.isPending;
    // A move drops what the old client owns (DD-049): say so before the save.
    const movedFrom =
      state.mode === "edit" && state.draft.clientId !== state.task.clientId ? state.task.clientId : null;
    const movedFromName = movedFrom ? clients?.find((client) => client.id === movedFrom)?.clientName : undefined;
    header = <h2 className={TITLE}>{creating ? "Nuovo task" : "Modifica task"}</h2>;
    // Creating, Crea waits for a running recording to stop, so it is never cut off.
    const recordingRuns = state.mode === "create" && unsentRecording && state.recording === null;
    const form = (
      <TaskForm
        draft={state.draft}
        onChange={drawer.changeDraft}
        onSubmit={creating ? handleCreate : handleSave}
        people={people}
        peopleLoading={peopleLoading}
        currentAssignee={state.mode === "edit" && !movedFrom ? state.task.assignee : null}
        clientOptions={clientOptions}
        clientNote={
          movedFrom
            ? `Spostando il task, i suoi collegamenti e i contatti di ${movedFromName ?? "questo cliente"} vengono rimossi.`
            : undefined
        }
        excludeClientContacts={excludeClientContacts}
        tagOptions={allPeople}
        canEditPeople={capabilities.canEditPeople}
        canTagTeam={capabilities.canTagTeam}
        canClearOwner={capabilities.canClearOwner}
        showClientVisible={capabilities.canSeeVisibility}
      />
    );
    body =
      creating && sections.notes ? (
        <div className="space-y-6">
          {form}
          <TaskCreateVoice onRecordingChange={drawer.holdRecording} onUnsentChange={handleUnsentChange} />
        </div>
      ) : (
        form
      );
    // Creating, the X closes and drops the draft (J12). Editing, Annulla goes back to the task, not out of the
    // drawer, so it stays.
    footer = (
      <>
        {!creating && (
          <Button type="button" variant="secondary" size="sm" onClick={drawer.cancel} disabled={pending}>
            Annulla
          </Button>
        )}
        <Button type="submit" form={TASK_FORM_ID} size="sm" disabled={!canSubmit || recordingRuns} isLoading={pending}>
          {creating ? "Crea" : "Salva"}
        </Button>
      </>
    );
  }

  // Any mode that holds a recording: the footer asks before the drawer drops it.
  if (pendingLeave) {
    footer = (
      <>
        <p className="mr-auto text-sm font-medium text-gray-900">Scartare la registrazione non inviata?</p>
        <Button type="button" variant="secondary" size="sm" onClick={() => setPendingLeave(null)}>
          Annulla
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={() => {
            const path = pathAfterDiscard(pendingLeave);
            setPendingLeave(null);
            close();
            if (path) navigate(path, navOptions);
          }}
        >
          Scarta
        </Button>
      </>
    );
  }

  return (
    <Drawer
      isOpen={state.mode !== "closed"}
      onClose={requestClose}
      header={header}
      footer={footer}
      bodyPadding
      a11yTitle={state.mode === "create" ? "Nuovo task" : "Task"}
      a11yDescription="Il task, i suoi collegamenti e le sue note."
      className="md:max-w-lg"
      onOpenAutoFocus={focusTitleInView}
    >
      {body}
    </Drawer>
  );
}
